import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import api, { apiError, assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ShoppingCart, Diamond, Package, PlayCircle, MagnifyingGlass, UserCircle, Phone, List, CaretDown, Cube, FacebookLogo, InstagramLogo, WhatsappLogo, ArrowsOutSimple } from "@phosphor-icons/react";

const Showcase3D = lazy(() => import("@/components/Showcase3D"));

// ---- Font system (Google Fonts) ----
export const FONTS = {
  serif: { label: "Serif clásica (Playfair)", stack: 'Georgia, "Playfair Display", serif', google: "Playfair+Display:ital,wght@0,400;0,600;0,700;1,400" },
  sans: { label: "Sans moderna (Inter)", stack: 'Inter, system-ui, sans-serif', google: "Inter:wght@400;500;600;700" },
  poppins: { label: "Poppins", stack: '"Poppins", sans-serif', google: "Poppins:wght@400;500;600;700" },
  montserrat: { label: "Montserrat", stack: '"Montserrat", sans-serif', google: "Montserrat:wght@400;500;600;700" },
  lora: { label: "Lora (serif)", stack: '"Lora", serif', google: "Lora:wght@400;500;600;700" },
  merriweather: { label: "Merriweather (serif)", stack: '"Merriweather", serif', google: "Merriweather:wght@400;700" },
  roboto: { label: "Roboto", stack: '"Roboto", sans-serif', google: "Roboto:wght@400;500;700" },
  oswald: { label: "Oswald (condensada)", stack: '"Oswald", sans-serif', google: "Oswald:wght@400;500;600;700" },
  bebas: { label: "Bebas Neue (títulos)", stack: '"Bebas Neue", sans-serif', google: "Bebas+Neue" },
  dancing: { label: "Dancing Script (cursiva)", stack: '"Dancing Script", cursive', google: "Dancing+Script:wght@400;600;700" },
  cormorant: { label: "Cormorant Garamond (elegante)", stack: '"Cormorant Garamond", Georgia, serif', google: "Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600" },
  ebgaramond: { label: "EB Garamond (clásica)", stack: '"EB Garamond", Georgia, serif', google: "EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400" },
  marcellus: { label: "Marcellus (titulares)", stack: '"Marcellus", Georgia, serif', google: "Marcellus" },
  cormorantsc: { label: "Cormorant SC (versalitas)", stack: '"Cormorant SC", Georgia, serif', google: "Cormorant+SC:wght@400;500;600;700" },
  questrial: { label: "Questrial (minimalista)", stack: '"Questrial", system-ui, sans-serif', google: "Questrial" },
  josefin: { label: "Josefin Sans (fina)", stack: '"Josefin Sans", system-ui, sans-serif', google: "Josefin+Sans:ital,wght@0,300;0,400;0,600;1,400" },
};
export function fontStack(key) { return (FONTS[key] || FONTS.serif).stack; }
export function catSlug(s) { return String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""); }
export function ensureFontsLoaded(keys = []) {
  if (typeof document === "undefined") return;
  const need = [...new Set(keys)].filter((k) => FONTS[k] && !["serif", "sans"].includes(k)).map((k) => FONTS[k].google);
  if (!need.length) return;
  const id = "hephy-gfonts";
  let link = document.getElementById(id);
  const href = `https://fonts.googleapis.com/css2?${need.map((g) => `family=${g}`).join("&")}&display=swap`;
  if (!link) { link = document.createElement("link"); link.id = id; link.rel = "stylesheet"; document.head.appendChild(link); }
  if (link.getAttribute("href") !== href) link.setAttribute("href", href);
}
export function collectFonts(site = {}) {
  const keys = [site.font || "serif"];
  const add = (b) => { if (b.style?.font) keys.push(b.style.font); if (b.style?.bodyFont) keys.push(b.style.bodyFont); if (b.style?.btnFont) keys.push(b.style.btnFont); };
  (site.pages || []).forEach((pg) => (pg.blocks || []).forEach(add));
  (site.blocks || []).forEach(add);
  return keys;
}

// ---- Block library (palette metadata + default factory) ----
export const BLOCK_GROUPS = [
  { label: "Portada", types: ["hero", "banners"] },
  { label: "Contenido", types: ["text", "image", "image_text", "columns", "divider", "html"] },
  { label: "Productos", types: ["products", "featured_product", "categories", "model3d"] },
  { label: "Medios", types: ["gallery", "video"] },
  { label: "Social", types: ["testimonials", "form"] },
  { label: "Blog", types: ["blog"] },
  { label: "Extras", types: ["button", "spacer"] },
];

export const BLOCK_META = {
  hero: { name: "Portada", desc: "Imagen grande con título y botón" },
  banners: { name: "Banners dobles", desc: "2–3 imágenes con título y botón" },
  text: { name: "Texto", desc: "Encabezado y párrafo" },
  image: { name: "Imagen", desc: "Una imagen con leyenda" },
  image_text: { name: "Imagen + Texto", desc: "Imagen y texto lado a lado" },
  columns: { name: "Columnas", desc: "2–3 columnas lado a lado" },
  divider: { name: "Divisor", desc: "Línea o separador" },
  html: { name: "HTML personalizado", desc: "Pega tu propio código" },
  products: { name: "Cuadrícula de productos", desc: "Muestra tus productos" },
  featured_product: { name: "Producto destacado", desc: "Resalta un producto" },
  categories: { name: "Comprar por categoría", desc: "Fila de tarjetas imagen + etiqueta" },
  model3d: { name: "Vitrina 3D", desc: "Producto 3D girando en vivo" },
  gallery: { name: "Galería", desc: "Varias imágenes en mosaico" },
  video: { name: "Video", desc: "Sube un video o incrusta YouTube" },
  testimonials: { name: "Testimonios", desc: "Reseñas de clientes" },
  form: { name: "Formulario", desc: "Captura mensajes/leads de clientes" },
  blog: { name: "Blog", desc: "Lista tus artículos publicados" },
  button: { name: "Botón", desc: "Llamada a la acción" },
  spacer: { name: "Espaciador", desc: "Espacio en blanco" },
};

const uid = () => "b_" + Math.random().toString(36).slice(2, 9);
export const emptyCol = (heading = "") => ({ image_asset_id: "", video_asset_id: "", video_url: "", heading, body: "Describe este punto brevemente.", cta_text: "", cta_link: "" });
export const emptyBanner = (title = "") => ({ image_asset_id: "", video_asset_id: "", video_url: "", title, title_color: "#ffffff", cta_text: "Ver más", cta_link: "#productos", btn_bg: "#000000", btn_color: "#ffffff", overlay: 35 });
export const emptyCat = (label = "") => ({ image_asset_id: "", label, link: "#productos" });

// Readable text color for a given background (dark bg -> light text, light bg -> dark text).
export function textOn(bg) {
  try {
    let h = (bg || "").replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? "#1a1a1a" : "#ececec";
  } catch { return "#ececec"; }
}
const baseStyle = (over = {}) => ({ bg: "", textColor: "", align: "left", paddingY: 56, maxWidth: "normal", radius: 16, ...over });

// Responsive grid columns. In the builder, ctx.device forces mobile/desktop layout so
// the toggle is visible in preview; on the live storefront (ctx.device undefined) real
// CSS breakpoints apply (mobileCols on small screens, desktopCols from md: up).
const _COLS = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4", 5: "grid-cols-5", 6: "grid-cols-6" };
const _MDCOLS = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5", 6: "md:grid-cols-6" };
export function responsiveGrid(ctx, mobileCols, desktopCols) {
  const m = _COLS[mobileCols] || "grid-cols-1";
  const d = _MDCOLS[desktopCols] || "md:grid-cols-3";
  if (ctx && ctx.device === "mobile") return m;
  if (ctx && ctx.device === "desktop") return _COLS[desktopCols] || "grid-cols-3";
  return `${m} ${d}`;
}


export function newBlock(type) {
  const id = uid();
  switch (type) {
    case "hero": return { id, type, style: baseStyle({ align: "left", paddingY: 0 }), props: { title: "Bienvenido a mi tienda", subtitle: "Joyería y productos personalizados a tu medida", cta_text: "Ver productos", cta_link: "#productos", image_asset_id: "", height: "md", overlay: 45 } };
    case "text": return { id, type, style: baseStyle({ maxWidth: "narrow" }), props: { heading: "Un título", body: "Escribe aquí tu texto. Cuenta la historia de tu marca, tus materiales o lo que te hace único." } };
    case "image": return { id, type, style: baseStyle(), props: { asset_id: "", caption: "", link: "", rounded: true, fit: "contain", height: 0, position: "center" } };
    case "image_text": return { id, type, style: baseStyle(), props: { asset_id: "", heading: "Sobre nosotros", body: "Describe tu taller, tu proceso o tu propuesta de valor.", image_side: "left", cta_text: "", cta_link: "" } };
    case "columns": return { id, type, style: baseStyle({ maxWidth: "wide" }), props: { count: 3, mobile_stack: true, cols: [emptyCol("Rápido"), emptyCol("Confiable"), emptyCol("Personalizado")] } };
    case "banners": return { id, type, style: baseStyle({ maxWidth: "wide", paddingY: 24 }), props: { count: 2, mobile_stack: true, items: [emptyBanner("Sortijas de Graduación"), emptyBanner("Anillos de Compromiso")] } };
    case "categories": return { id, type, style: baseStyle({ maxWidth: "wide", align: "center" }), props: { heading: "Comprar por Categoría", count: 4, items: [emptyCat("Redondo"), emptyCat("Cojín"), emptyCat("Ovalado"), emptyCat("Esmeralda")] } };
    case "model3d": return { id, type, style: baseStyle({ maxWidth: "normal", align: "center" }), props: { heading: "Explóralo en 3D", product_id: "", autorotate: true, height: "lg" } };
    case "divider": return { id, type, style: baseStyle({ paddingY: 24 }), props: { thickness: 1, color: "", width: "normal" } };
    case "html": return { id, type, style: baseStyle(), props: { code: "<!-- Pega tu HTML aquí -->" } };
    case "products": return { id, type, style: baseStyle({ maxWidth: "wide" }), props: { heading: "Nuestros productos", columns: 3, cols_mobile: 1, limit: 0, filter: "all" } };
    case "featured_product": return { id, type, style: baseStyle(), props: { heading: "Destacado", product_id: "" } };    case "gallery": return { id, type, style: baseStyle({ maxWidth: "wide" }), props: { heading: "Galería", asset_ids: [], columns: 3 } };    case "video": return { id, type, style: baseStyle({ maxWidth: "narrow" }), props: { heading: "", youtube_url: "" } };
    case "testimonials": return { id, type, style: baseStyle({ maxWidth: "wide" }), props: { heading: "Lo que dicen", items: [{ name: "Cliente feliz", text: "¡Me encantó el resultado!" }] } };
    case "form": return { id, type, style: baseStyle({ maxWidth: "narrow" }), props: { heading: "Contáctanos", description: "Déjanos tus datos y te responderemos pronto.", show_phone: true, show_message: true, fields: [], button_text: "Enviar", success_text: "¡Gracias! Te contactaremos pronto.", source: "Formulario de contacto" } };
    case "blog": return { id, type, style: baseStyle({ maxWidth: "wide" }), props: { heading: "Blog", columns: 3, limit: 6 } };
    case "button": return { id, type, style: baseStyle({ align: "center", paddingY: 32 }), props: { text: "Haz clic aquí", link: "#productos", fullwidth: false } };
    case "spacer": return { id, type, style: baseStyle({ paddingY: 0 }), props: { height: 48 } };
    default: return { id, type, style: baseStyle(), props: {} };
  }
}

const MAXW = { narrow: "max-w-2xl", normal: "max-w-4xl", wide: "max-w-6xl", full: "max-w-none" };
const ALIGN = { left: "text-left items-start", center: "text-center items-center", right: "text-right items-end" };

function hasBgVideo(style) { return !!(style && (style.bg_video_asset_id || style.bg_video_url)); }
function sectionStyle(style, ctx) {
  const s = {};
  if (style.bg) s.background = style.bg;
  if (style.textColor) s.color = style.textColor;
  if (style.font) s.fontFamily = fontStack(style.font);
  s.paddingTop = `${style.paddingY ?? 56}px`;
  s.paddingBottom = `${style.paddingY ?? 56}px`;
  if (style.minH) { s.minHeight = `${style.minH}px`; s.display = "flex"; s.flexDirection = "column"; s.justifyContent = "center"; }
  if (hasBgVideo(style)) { s.position = "relative"; s.overflow = "hidden"; }
  return s;
}
function SectionBg({ style }) {
  if (!hasBgVideo(style)) return null;
  return (
    <>
      <BgVideo assetId={style.bg_video_asset_id} url={style.bg_video_url} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: `rgba(0,0,0,${(style.bg_video_overlay ?? 35) / 100})` }} />
    </>
  );
}

function ytId(url = "") {
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : (url.trim().length === 11 ? url.trim() : "");
}

// Product cards -------------------------------------------------
const BADGE_POS = { "top-left": "top-3 left-3", "top-right": "top-3 right-3", "top-center": "top-3 left-1/2 -translate-x-1/2", "bottom-left": "bottom-3 left-3", "bottom-right": "bottom-3 right-3", "bottom-center": "bottom-3 left-1/2 -translate-x-1/2" };

function Product3DCard({ p, slug, accent, interactive, opts = {} }) {
  const titlePos = opts.title_pos || "below";
  const imgH = opts.img_h ? { height: `${opts.img_h}px` } : null;
  const imgCls = opts.img_h ? "" : "aspect-square";
  const titleStyle = { ...(opts.title_size ? { fontSize: `${opts.title_size}px`, lineHeight: 1.15 } : {}), ...(opts.title_font ? { fontFamily: opts.title_font } : {}) };
  const priceStyle = opts.price_size ? { fontSize: `${opts.price_size}px` } : null;
  const inner = (
    <>
      <div className={`${imgCls} bg-neutral-900/60 relative overflow-hidden`} style={imgH}>
        {p.base_url
          ? <img src={assetUrl(p.base_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          : <div className="w-full h-full flex items-center justify-center"><Diamond size={36} weight="duotone" className="text-neutral-700" /></div>}
        {p.has_3d && opts.show_badge !== false && <span className={`absolute ${BADGE_POS[opts.badge_pos] || BADGE_POS["top-left"]} inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold text-black shadow-lg`} style={{ backgroundColor: accent }}><Cube size={11} weight="fill" /> Personalizable 3D</span>}
        {p.sale_percent > 0 && <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-1 rounded-full bg-rose-500 text-white shadow-lg" data-testid={`sale-badge-${p.id}`}>−{p.sale_percent}%</span>}
        {titlePos === "overlay" && <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/75 to-transparent"><h3 className="font-semibold text-white truncate" style={titleStyle}>{p.name}</h3></div>}
      </div>
      {titlePos !== "hidden" && (
        <div className={`p-4 ${opts.text_align === "center" ? "text-center" : ""}`}>
          {titlePos === "below" && <h3 className="font-semibold truncate" style={titleStyle}>{p.name}</h3>}
          <div className={`mt-2 ${opts.text_align === "center" ? "flex flex-col items-center gap-1" : "flex items-center justify-between"}`}>
            <span className="opacity-70" style={priceStyle || { fontSize: 14 }}>{p.price > 0 ? (p.sale_percent > 0 ? <>Desde <span className="font-semibold" style={{ color: accent }}>${Number(p.sale_price).toLocaleString()}</span> <span className="line-through opacity-50">${Number(p.price).toLocaleString()}</span></> : `Desde $${Number(p.price).toLocaleString()}`) : "Personalizable"}</span>
            {opts.show_cta !== false && <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: accent }}>Personalizar <ArrowRight size={13} /></span>}
          </div>
        </div>
      )}
    </>
  );
  const plain = opts.card_style === "plain";
  const cls = `group block overflow-hidden ${plain ? "" : "border border-black/10 dark:border-white/10 bg-white/5"} ${opts.rounded === false ? "" : "rounded-2xl"} hover:-translate-y-1 transition-all h-full`;
  if (!interactive) return <div className={cls} data-testid={`store-product-${p.id}`}>{inner}</div>;
  return <Link to={`/tienda/${slug}/producto/${p.id}`} className={cls} data-testid={`store-product-${p.id}`}>{inner}</Link>;
}

function SimpleCard({ p, slug, accent, interactive, onAdded, opts = {} }) {
  const variants = p.variants || [];
  const [variant, setVariant] = useState(variants[0]?.label || "");
  const [busy, setBusy] = useState(false);
  const titlePos = opts.title_pos || "below";
  const imgH = opts.img_h ? { height: `${opts.img_h}px` } : null;
  const imgCls = opts.img_h ? "" : "aspect-square";
  const titleStyle = { ...(opts.title_size ? { fontSize: `${opts.title_size}px`, lineHeight: 1.15 } : {}), ...(opts.title_font ? { fontFamily: opts.title_font } : {}) };
  const priceStyle = opts.price_size ? { fontSize: `${opts.price_size}px` } : null;
  const priceOf = () => { const v = variants.find((x) => String(x.label) === String(variant)); return Number((v && v.price) || p.price || 0); };
  const add = async () => {
    if (!interactive) return;
    setBusy(true);
    try {
      const token = localStorage.getItem(`hephy_cart_${slug}`) || undefined;
      const { data } = await api.post("/public/cart/add", { token, slug, product_id: p.id, metal_id: "", variant_label: variant, quantity: 1 }, { withCredentials: false });
      localStorage.setItem(`hephy_cart_${slug}`, data.token);
      onAdded && onAdded(data.count || 0);
      toast.success("Añadido al carrito");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`group overflow-hidden ${opts.card_style === "plain" ? "" : "border border-black/10 dark:border-white/10 bg-white/5"} ${opts.rounded === false ? "" : "rounded-2xl"} h-full`} data-testid={`store-product-${p.id}`}>
      <Link to={`/tienda/${slug}/producto/${p.id}`} className={`block ${imgCls} bg-neutral-900/60 relative overflow-hidden`} style={imgH}>
        {(p.image_url || p.base_url)
          ? <img src={assetUrl(p.image_url || p.base_url)} alt={p.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Package size={36} weight="duotone" className="text-neutral-700" /></div>}
        {p.catalog_type === "digital" && opts.show_badge !== false && <span className={`absolute ${BADGE_POS[opts.badge_pos] || BADGE_POS["top-left"]} text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold text-black`} style={{ backgroundColor: accent }}>Digital</span>}
        {p.sale_percent > 0 && <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-1 rounded-full bg-rose-500 text-white shadow-lg" data-testid={`sale-badge-${p.id}`}>−{p.sale_percent}%</span>}
        {titlePos === "overlay" && <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/75 to-transparent"><h3 className="font-semibold text-white truncate" style={titleStyle}>{p.name}</h3></div>}
      </Link>
      <div className={`p-4 ${opts.text_align === "center" ? "text-center" : ""}`}>
        {titlePos === "below" && <Link to={`/tienda/${slug}/producto/${p.id}`} className="block hover:opacity-80 transition-opacity"><h3 className="font-semibold truncate" style={titleStyle}>{p.name}</h3></Link>}
        {variants.length > 0 && (
          <select value={variant} onChange={(e) => setVariant(e.target.value)} data-testid={`variant-select-${p.id}`} className="mt-2 w-full h-9 rounded-lg bg-black/20 border border-white/15 px-2 text-sm">
            {variants.map((v) => <option key={v.label} value={v.label} className="bg-[#1a1a1a]">{p.variant_name || "Opción"}: {v.label}{v.price ? ` — $${Number(v.price).toLocaleString()}` : ""}</option>)}
          </select>
        )}
        <div className={`mt-2 gap-2 ${opts.text_align === "center" ? "flex flex-col items-center" : "flex items-center justify-between"}`}>
          <span className="font-semibold opacity-80" style={priceStyle || { fontSize: 14 }}>{p.sale_percent > 0 ? <><span style={{ color: accent }}>${Math.round(priceOf() * (1 - p.sale_percent / 100) * 100) / 100}</span> <span className="line-through opacity-50 font-normal">${priceOf().toLocaleString()}</span></> : `$${priceOf().toLocaleString()}`}</span>
          {opts.show_cta !== false && <button onClick={add} disabled={busy} data-testid={`add-cart-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-full text-black disabled:opacity-60" style={{ backgroundColor: accent }}>
            <ShoppingCart size={13} weight="fill" /> {busy ? "…" : "Añadir"}
          </button>}
        </div>
      </div>
    </div>
  );
}

function ProductCard({ p, ctx, opts }) {
  return (p.catalog_type && p.catalog_type !== "custom3d")
    ? <SimpleCard p={p} slug={ctx.slug} accent={ctx.accent} interactive={ctx.interactive} onAdded={ctx.onAdded} opts={opts} />
    : <Product3DCard p={p} slug={ctx.slug} accent={ctx.accent} interactive={ctx.interactive} opts={opts} />;
}

function ProductGridItem({ p, ctx, opts, colCount, gridRef, size, onCommit, canResize }) {
  const [st, onDown] = useGridItem(size.span, size.h, onCommit, { gridRef, colCount });
  const sp = Math.min(st.span || 1, colCount || 1);
  return (
    <div className="relative" style={{ gridColumn: `span ${sp} / span ${sp}` }}>
      <ProductCard p={p} ctx={ctx} opts={{ ...opts, img_h: st.h || opts.img_h }} />
      {canResize && <CornerHandle onDown={onDown} testid={`product-resize-${p.id}`} />}
    </div>
  );
}

function ProductsGrid({ list, ctx, opts, colCount, colsCls, canResize, sizeFor, commitFor }) {
  const gridRef = useRef(null);
  return (
    <div ref={gridRef} className={`grid ${colsCls} gap-5`} style={{ alignItems: "start" }} data-testid="store-products-grid">
      {list.map((p) => <ProductGridItem key={p.id} p={p} ctx={ctx} opts={opts} colCount={colCount} gridRef={gridRef} size={sizeFor(p)} onCommit={commitFor(p)} canResize={canResize} />)}
    </div>
  );
}

// Contact form rendered inside a column (labels on top, 2-up fields, like a classic contact layout).
function ColumnForm({ col, ctx, index, bodyStyle }) {
  const { accent, slug } = ctx;
  const fm = col.form || {};
  const [f, setF] = useState({ name: "", last: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const showLast = fm.show_last !== false, showPhone = fm.show_phone !== false, showMsg = fm.show_message !== false;
  const submit = async (e) => {
    e.preventDefault();
    if (!ctx.interactive) return;
    if (!f.name.trim() || !f.email.trim()) { toast.error("Completa nombre y email."); return; }
    if (showMsg && !f.message.trim()) { toast.error("Escribe tu mensaje."); return; }
    setBusy(true);
    try {
      await api.post(`/public/store/${slug}/lead`, { name: [f.name, showLast ? f.last : ""].filter(Boolean).join(" ").trim(), email: f.email, phone: f.phone, message: f.message, source: fm.source || "Formulario (columna)" }, { withCredentials: false });
      setSent(true);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const bc = { borderColor: fm.field_border || "rgba(128,128,128,.6)", borderRadius: fm.rounded === false ? 0 : 6, color: "inherit" };
  const fld = "w-full h-10 px-3 text-sm bg-transparent border outline-none focus:opacity-100";
  const L = ({ children }) => <label className="block text-xs mb-1 opacity-80">{children}</label>;
  const btnBg = fm.btn_bg || accent;
  if (sent) return <div className="w-full rounded-lg border p-5 text-sm" style={{ ...bc, ...bodyStyle }} data-testid={`col-form-success-${index}`}>{fm.success_text || "¡Gracias! Te contactaremos pronto."}</div>;
  return (
    <form onSubmit={submit} className="w-full text-left space-y-4" style={bodyStyle} data-testid={`col-form-${index}`}>
      {fm.heading && <div className="text-sm font-medium mb-2">{fm.heading}</div>}
      <div className={`grid ${showLast ? "grid-cols-2" : "grid-cols-1"} gap-4`}>
        <div><L>Nombre *</L><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={fld} style={bc} data-testid={`col-form-name-${index}`} /></div>
        {showLast && <div><L>Apellidos</L><input value={f.last} onChange={(e) => setF({ ...f, last: e.target.value })} className={fld} style={bc} data-testid={`col-form-last-${index}`} /></div>}
      </div>
      <div className={`grid ${showPhone ? "grid-cols-2" : "grid-cols-1"} gap-4`}>
        <div><L>Email *</L><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={fld} style={bc} data-testid={`col-form-email-${index}`} /></div>
        {showPhone && <div><L>Teléfono</L><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={fld} style={bc} data-testid={`col-form-phone-${index}`} /></div>}
      </div>
      {showMsg && <div><L>Mensaje *</L><textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={3} className={`${fld} h-auto py-2`} style={bc} data-testid={`col-form-message-${index}`} /></div>}
      <button type="submit" disabled={busy} className="px-8 py-2.5 text-sm font-medium rounded-full disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ backgroundColor: btnBg, color: fm.btn_color || textOn(btnBg) }} data-testid={`col-form-submit-${index}`}>{busy ? "Enviando…" : (fm.button_text || "Enviar")}</button>
    </form>
  );
}

function FormBlock({ block, ctx }) {
  const { props = {}, style = {} } = block;
  const { accent, headingFont, slug, titleColor } = ctx;
  const customFields = Array.isArray(props.fields) ? props.fields : [];
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [extra, setExtra] = useState({});
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!ctx.interactive) return;
    if (!f.name.trim()) { toast.error("Ingresa tu nombre."); return; }
    for (const cf of customFields) {
      if (cf.required && !(extra[cf.id] || "").trim()) { toast.error(`Completa: ${cf.label || "campo"}`); return; }
    }
    setBusy(true);
    try {
      const extraLines = customFields.map((cf) => (extra[cf.id] || "").trim() ? `${cf.label || "Campo"}: ${extra[cf.id]}` : "").filter(Boolean).join("\n");
      const message = [f.message, extraLines].filter(Boolean).join("\n");
      await api.post(`/public/store/${slug}/lead`, { name: f.name, email: f.email, phone: f.phone, message, source: props.source || "Formulario" }, { withCredentials: false });
      setSent(true);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const fieldCls = "w-full rounded-lg px-3 py-2.5 text-sm bg-black/20 border border-white/15 focus:border-white/40 outline-none";
  const setEx = (id, v) => setExtra((s) => ({ ...s, [id]: v }));
  return (
    <section style={{ ...(style.bg ? { background: style.bg } : {}), paddingTop: `${style.paddingY ?? 56}px`, paddingBottom: `${style.paddingY ?? 56}px` }} data-testid="blk-form">
      <div className="max-w-xl mx-auto px-6">
        {props.heading && <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-center" style={{ fontFamily: style.font ? fontStack(style.font) : headingFont, color: style.textColor || titleColor || accent }}>{props.heading}</h2>}
        {props.description && <p className="text-sm opacity-80 mb-6 text-center">{props.description}</p>}
        {sent
          ? <div className="rounded-xl border border-white/15 bg-white/5 p-6 text-center" data-testid="form-success">{props.success_text || "¡Gracias!"}</div>
          : (
            <form onSubmit={submit} className="space-y-3" data-testid="store-lead-form">
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nombre *" className={fieldCls} data-testid="form-name" />
              {props.show_email !== false && <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Correo" type="email" className={fieldCls} data-testid="form-email" />}
              {props.show_phone !== false && <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Teléfono" className={fieldCls} data-testid="form-phone" />}
              {customFields.map((cf) => (
                cf.type === "textarea"
                  ? <textarea key={cf.id} value={extra[cf.id] || ""} onChange={(e) => setEx(cf.id, e.target.value)} placeholder={(cf.label || "") + (cf.required ? " *" : "")} rows={3} className={fieldCls} data-testid={`form-field-${cf.id}`} />
                  : cf.type === "select"
                    ? <select key={cf.id} value={extra[cf.id] || ""} onChange={(e) => setEx(cf.id, e.target.value)} className={fieldCls} data-testid={`form-field-${cf.id}`}><option value="" className="bg-[#1a1a1a]">{(cf.label || "Elegir") + (cf.required ? " *" : "")}</option>{(cf.options || []).map((o, oi) => <option key={oi} value={o} className="bg-[#1a1a1a]">{o}</option>)}</select>
                    : <input key={cf.id} type={cf.type === "number" ? "number" : "text"} value={extra[cf.id] || ""} onChange={(e) => setEx(cf.id, e.target.value)} placeholder={(cf.label || "") + (cf.required ? " *" : "")} className={fieldCls} data-testid={`form-field-${cf.id}`} />
              ))}
              {props.show_message !== false && <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Mensaje" rows={4} className={fieldCls} data-testid="form-message" />}
              <button type="submit" disabled={busy} className="w-full rounded-full py-3 text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: props.btn_bg || accent, color: props.btn_color || textOn(props.btn_bg || accent) }} data-testid="form-submit">{busy ? "Enviando…" : (props.button_text || "Enviar")}</button>
            </form>
          )}
      </div>
    </section>
  );
}

function BlogBlock({ block, ctx }) {
  const { props = {}, style = {} } = block;
  const { accent, headingFont, slug, titleColor } = ctx;
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (!slug) return;
    api.get(`/public/store/${slug}/blog`, { withCredentials: false }).then((r) => setPosts(r.data || [])).catch(() => setPosts([]));
  }, [slug]);
  const tr = ctx.translate || ((x) => x);
  const list = (props.limit > 0 ? posts.slice(0, props.limit) : posts).map(tr);
  const cols = responsiveGrid(ctx, props.cols_mobile || 1, props.columns || 3);
  const innerCls = `${{ narrow: "max-w-2xl", normal: "max-w-4xl", wide: "max-w-6xl", full: "max-w-none" }[style.maxWidth] || "max-w-6xl"} mx-auto px-6`;
  const Card = ({ p }) => (
    <>
      <div className="aspect-[16/10] bg-neutral-900/50 overflow-hidden">
        {p.cover_url ? <img src={assetUrl(p.cover_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${ctx.bg} 0%, ${accent}33 100%)` }} />}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-lg" style={{ fontFamily: headingFont }}>{p.title}</h3>
        {p.excerpt && <p className="text-sm opacity-75 mt-1 line-clamp-2">{p.excerpt}</p>}
        <span className="inline-flex items-center gap-1 text-xs font-medium mt-3" style={{ color: accent }}>Leer más <ArrowRight size={12} /></span>
      </div>
    </>
  );
  return (
    <section style={{ ...(style.bg ? { background: style.bg } : {}), paddingTop: `${style.paddingY ?? 56}px`, paddingBottom: `${style.paddingY ?? 56}px` }} data-testid="blk-blog">
      <div className={innerCls}>
        {props.heading && <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: style.font ? fontStack(style.font) : headingFont, color: style.textColor || titleColor || accent }}>{props.heading}</h2>}
        {list.length === 0
          ? <div className="border border-dashed border-white/15 rounded-2xl p-12 text-center text-neutral-500 text-sm">Aún no hay artículos publicados.</div>
          : <div className={`grid ${cols} gap-5`} data-testid="blog-grid">{list.map((p) => ctx.interactive
              ? <Link key={p.id} to={`/tienda/${slug}/blog/${p.slug}`} className="group block overflow-hidden rounded-2xl border border-white/10 bg-white/5 hover:-translate-y-1 transition-all"><Card p={p} /></Link>
              : <div key={p.id} className="group block overflow-hidden rounded-2xl border border-white/10 bg-white/5"><Card p={p} /></div>)}</div>}
      </div>
    </section>
  );
}

function useIsMobileViewport() {
  const [m, setM] = useState(typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const h = (e) => setM(e.matches);
    mq.addEventListener ? mq.addEventListener("change", h) : mq.addListener(h);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", h) : mq.removeListener(h); };
  }, []);
  return m;
}

function Model3DBlock({ block, ctx }) {
  const { props = {}, style = {} } = block;
  const { accent, headingFont, products = [], slug, interactive, titleColor } = ctx;
  const isMobileVp = useIsMobileViewport();
  const mobile = ctx.device ? ctx.device === "mobile" : isMobileVp;
  const p = products.find((x) => x.id === props.product_id && x.model_url) || products.find((x) => x.id === props.product_id) || null;
  const legacyMap = { sm: 320, md: 500, lg: 700 };
  const hDesktop = props.h_desktop || legacyMap[props.height || "lg"];
  const hMobile = props.h_mobile || 340;
  const stageH = mobile ? hMobile : hDesktop;
  const innerCls = `${MAXW[style.maxWidth] || MAXW.normal} mx-auto px-6 w-full`;
  const canCustomize = p && p.has_3d && props.show_cta !== false;
  const ctaLabel = props.cta_label || "Personalizar este";
  const btnStyle = { backgroundColor: accent, color: textOn(accent), fontFamily: style.font ? fontStack(style.font) : undefined };
  const btnCls = "mt-3 sm:mt-6 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-semibold hover:opacity-90 transition-opacity";
  const canResize = !!ctx.onUpdateBlock;
  const rad = props.rounded === false ? 0 : 16;
  const stageRef = useRef(null);
  const [box, onCorner] = useDragBox(props.width_px, stageH, ({ w, h }) => ctx.onUpdateBlock(block.id, { width_px: w, h_desktop: h }), { measure: () => stageRef.current ? { w: stageRef.current.getBoundingClientRect().width, h: stageRef.current.getBoundingClientRect().height } : null });
  const stageW = box.w;
  return (
    <section style={sectionStyle(style, ctx)} data-testid="blk-model3d">
      <SectionBg style={style} />
      <div className={`${innerCls} text-center ${hasBgVideo(style) ? "relative z-10" : ""}`} style={{ textAlign: style.align }}>
        {props.heading && <h2 className="text-2xl sm:text-3xl font-bold mb-5" style={{ fontFamily: style.font ? fontStack(style.font) : headingFont, color: style.textColor || titleColor || accent, textAlign: style.align }}>{props.heading}</h2>}
        <div ref={stageRef} className="relative overflow-hidden mx-auto" style={{ width: stageW ? `${stageW}px` : "100%", maxWidth: "100%", height: stageH + "px", borderRadius: rad, background: `radial-gradient(circle at 50% 38%, ${accent}22, transparent 70%)` }} data-testid="model3d-stage">
          {p && (p.model_parts?.length || p.model_url)
            ? (p.model_parts?.length
                ? <iframe title={`3d-${p.id}`} src={`/embed/${p.id}?store=${slug || ""}&showcase=1`} className="absolute inset-0 w-full h-full border-0" scrolling="no" loading="lazy" data-testid="model3d-iframe" />
                : <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center text-sm opacity-60">Cargando 3D…</div>}><Showcase3D modelUrl={p.model_url} autorotate={props.autorotate !== false} accent={accent} interactive={ctx.interactive} baseColor={props.base_color || undefined} logoColor={props.logo_color || undefined} metallic={props.metallic !== false && (p.material_family || "metal") === "metal"} /></Suspense>)
            : <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-500 text-sm gap-2"><Diamond size={30} weight="duotone" /> Selecciona un producto con modelo 3D</div>}
          {canResize && <CornerHandle onDown={onCorner} testid={`model3d-resize-${block.id}`} />}
        </div>
        {canCustomize && (interactive
          ? <Link to={`/embed/${p.id}?store=${slug}`} className={btnCls} style={btnStyle} data-testid="model3d-customize-btn">{ctaLabel} <ArrowRight size={15} /></Link>
          : <span className={btnCls} style={btnStyle} data-testid="model3d-customize-btn">{ctaLabel} <ArrowRight size={15} /></span>)}
      </div>
    </section>
  );
}

// Main block renderer -------------------------------------------
// Shared drag-to-resize helpers (used only in the builder, where ctx.onUpdateBlock exists)
export function ResizeHandleBar({ onDown, testid, label = "Arrastra para ajustar el alto" }) {
  return (
    <div onPointerDown={onDown} onPointerDownCapture={(e) => e.stopPropagation()} title={label}
      className="pointer-events-auto absolute left-1/2 -translate-x-1/2 bottom-1.5 flex items-center justify-center gap-0.5 w-16 h-6 rounded-full bg-black/75 border border-white/30 text-white cursor-ns-resize touch-none hover:bg-[#d4af37] hover:text-black hover:border-[#d4af37] transition-colors z-30 shadow-lg"
      data-testid={testid}>
      <span className="w-7 h-0.5 bg-current rounded opacity-80" /><span className="w-7 h-0.5 bg-current rounded opacity-80" />
    </div>
  );
}

function CornerHandle({ onDown, testid, label = "Arrastra para cambiar el tamaño (ancho y alto)" }) {
  return (
    <div onPointerDown={onDown} onPointerDownCapture={(e) => e.stopPropagation()} title={label}
      className="pointer-events-auto absolute right-1 bottom-1 w-7 h-7 rounded-md bg-black/75 border border-white/40 text-white flex items-center justify-center cursor-nwse-resize touch-none hover:bg-[#d4af37] hover:text-black hover:border-[#d4af37] transition-colors z-30 shadow-lg"
      data-testid={testid}>
      <ArrowsOutSimple size={14} weight="bold" style={{ transform: "rotate(90deg)" }} />
    </div>
  );
}

function useDragHeight(initial, commit, { min = 100, max = 1400, measure } = {}) {
  const [h, setH] = useState(initial || 0);
  const hRef = useRef(initial || 0);
  useEffect(() => { setH(initial || 0); hRef.current = initial || 0; }, [initial]);
  const onDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const startY = e.clientY;
    const base = hRef.current || (measure && measure()) || 320;
    const move = (ev) => { const nh = Math.max(min, Math.min(max, Math.round(base + (ev.clientY - startY)))); hRef.current = nh; setH(nh); };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); commit(hRef.current); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  return [h, onDown];
}

// Free corner resize: width + height
function useDragBox(initialW, initialH, commit, { measure } = {}) {
  const [box, setBox] = useState({ w: initialW || 0, h: initialH || 0 });
  const ref = useRef({ w: initialW || 0, h: initialH || 0 });
  useEffect(() => { const b = { w: initialW || 0, h: initialH || 0 }; setBox(b); ref.current = b; }, [initialW, initialH]);
  const onDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sy = e.clientY;
    const m = measure && measure();
    const baseW = ref.current.w || (m && m.w) || 480;
    const baseH = ref.current.h || (m && m.h) || 320;
    const move = (ev) => {
      const nw = Math.max(80, Math.min(2000, Math.round(baseW + (ev.clientX - sx))));
      const nh = Math.max(60, Math.min(1600, Math.round(baseH + (ev.clientY - sy))));
      ref.current = { w: nw, h: nh }; setBox({ w: nw, h: nh });
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); commit(ref.current); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  return [box, onDown];
}

// Grid item resize: column span (width) + height, snapped to the grid columns
function useGridItem(initialSpan, initialH, commit, { gridRef, colCount }) {
  const [st, setSt] = useState({ span: initialSpan || 1, h: initialH || 0 });
  const ref = useRef({ span: initialSpan || 1, h: initialH || 0 });
  useEffect(() => { const s = { span: initialSpan || 1, h: initialH || 0 }; setSt(s); ref.current = s; }, [initialSpan, initialH]);
  const onDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sy = e.clientY;
    const gw = gridRef.current?.getBoundingClientRect().width || 600;
    const colW = Math.max(40, gw / (colCount || 1));
    const baseSpan = ref.current.span || 1;
    const baseWpx = baseSpan * colW;
    const baseH = ref.current.h || colW;
    const move = (ev) => {
      const nspan = Math.max(1, Math.min(colCount || 1, Math.round((baseWpx + (ev.clientX - sx)) / colW)));
      const nh = Math.max(60, Math.min(1200, Math.round(baseH + (ev.clientY - sy))));
      ref.current = { span: nspan, h: nh }; setSt({ span: nspan, h: nh });
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); commit(ref.current); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  return [st, onDown];
}

function GridMediaItem({ canResize, colCount, gridRef, span, h, aspectFallback, onCommit, children, after, boxClassName = "", boxStyle = {}, cellClassName = "", href, interactive, testid }) {
  const [st, onDown] = useGridItem(span, h, onCommit, { gridRef, colCount });
  const sp = Math.min(st.span || 1, colCount || 1);
  const box = (
    <div className={`relative ${st.h ? "" : aspectFallback} ${boxClassName}`} style={{ ...boxStyle, height: st.h ? `${st.h}px` : undefined }}>
      {children}
      {canResize && <CornerHandle onDown={onDown} testid={testid} />}
    </div>
  );
  const cellStyle = { gridColumn: `span ${sp} / span ${sp}` };
  return (href && interactive)
    ? <a href={href} className={cellClassName} style={cellStyle}>{box}{after}</a>
    : <div className={cellClassName} style={cellStyle}>{box}{after}</div>;
}

// Background video layer for a section/column
function BgVideo({ assetId, url }) {
  const vid = ytId(url);
  if (assetId) return <video src={assetUrl(`/api/assets/${assetId}`)} className="absolute inset-0 w-full h-full object-cover pointer-events-none" autoPlay muted loop playsInline preload="metadata" />;
  if (vid) return <iframe className="absolute inset-0 w-full h-full pointer-events-none" style={{ border: 0 }} src={`https://www.youtube.com/embed/${vid}?autoplay=1&mute=1&loop=1&playlist=${vid}&controls=0&playsinline=1`} title="fondo" allow="autoplay; encrypted-media" />;
  return null;
}

function ResizableImage({ block, ctx, style, props }) {
  const fit = props.fit || "contain";
  const canResize = !!ctx.onUpdateBlock;
  const radius = props.rounded ? (style.radius ?? 16) : 0;
  const imgRef = useRef(null);
  const [box, onCorner] = useDragBox(props.width_px, props.height, ({ w, h }) => ctx.onUpdateBlock(block.id, { width_px: w, height: h }), { measure: () => imgRef.current ? { w: imgRef.current.getBoundingClientRect().width, h: imgRef.current.getBoundingClientRect().height } : null });
  if (!props.asset_id) return <div className="w-full aspect-[16/9] rounded-2xl bg-white/5 border border-dashed border-white/15 flex items-center justify-center text-neutral-500 text-sm">Imagen</div>;
  const w = box.w, h = box.h;
  const imgStyle = fit === "cover"
    ? { width: "100%", height: (h || 320) + "px", objectFit: "cover", objectPosition: props.position || "center", borderRadius: radius }
    : { width: "100%", height: h ? h + "px" : "auto", maxHeight: h ? undefined : "80vh", objectFit: "contain", borderRadius: radius };
  const img = <img ref={imgRef} src={assetUrl(`/api/assets/${props.asset_id}`)} alt={props.caption || ""} style={imgStyle} data-testid="image-block-img" />;
  const inner = props.link && ctx.interactive ? <a href={props.link}>{img}</a> : img;
  return (
    <div className="relative" style={{ width: w ? `${w}px` : "100%", maxWidth: "100%" }}>
      {inner}
      {canResize && <CornerHandle onDown={onCorner} testid={`image-resize-${block.id}`} />}
    </div>
  );
}

function ResizableVideo({ block, ctx, props }) {
  const ref = useRef(null);
  const canResize = !!ctx.onUpdateBlock;
  const rad = props.rounded === false ? 0 : 16;
  const [box, onCorner] = useDragBox(props.width_px, props.height_px, ({ w, h }) => ctx.onUpdateBlock(block.id, { width_px: w, height_px: h }), { measure: () => ref.current ? { w: ref.current.getBoundingClientRect().width, h: ref.current.getBoundingClientRect().height } : null });
  const w = box.w, h = box.h;
  return (
    <div ref={ref} className="relative overflow-hidden bg-black" style={{ width: w ? `${w}px` : "100%", maxWidth: "100%", height: h ? `${h}px` : undefined, borderRadius: rad }}>
      <video src={assetUrl(`/api/assets/${props.asset_id}`)} poster={props.poster_asset_id ? assetUrl(`/api/assets/${props.poster_asset_id}`) : undefined} className={`w-full ${h ? "h-full object-cover" : ""}`} controls={props.controls !== false} autoPlay={!!props.autoplay} muted={!!props.autoplay || !!props.muted} loop={!!props.loop} playsInline preload="metadata" data-testid="blk-video" />
      {canResize && <CornerHandle onDown={onCorner} testid={`video-resize-${block.id}`} />}
    </div>
  );
}

function ColumnMedia({ block, ctx, col, index, allCols, rounded = true }) {
  const ref = useRef(null);
  const canResize = !!ctx.onUpdateBlock;
  const rad = (rounded === false || col.rounded === false) ? 0 : 14;
  const commit = ({ w, h }) => { const next = (allCols || []).map((c, j) => j === index ? { ...c, media_w: w, media_h: h } : c); ctx.onUpdateBlock(block.id, { cols: next }); };
  const [box, onCorner] = useDragBox(col.media_w, col.media_h, commit, { measure: () => ref.current ? { w: ref.current.getBoundingClientRect().width, h: ref.current.getBoundingClientRect().height } : null });
  const w = box.w, h = box.h;
  const cvid = ytId(col.video_url);
  const mcls = h ? "object-cover" : "aspect-video object-cover";
  const boxStyle = { width: w ? `${w}px` : "100%", maxWidth: "100%", height: h ? `${h}px` : undefined, borderRadius: rad };
  let media = null;
  if (col.model_product_id) {
    media = <div ref={ref} className="relative w-full overflow-hidden bg-black/40" style={{ ...boxStyle, height: h ? `${h}px` : undefined, aspectRatio: h ? undefined : "1 / 1" }}><iframe title={`col3d-${index}`} src={`/embed/${col.model_product_id}?store=${ctx.slug || ""}&showcase=1`} className="absolute inset-0 w-full h-full border-0" scrolling="no" loading="lazy" data-testid={`col-model3d-${index}`} /></div>;
  } else if (col.video_asset_id) media = <video ref={ref} src={assetUrl(`/api/assets/${col.video_asset_id}`)} poster={col.poster_asset_id ? assetUrl(`/api/assets/${col.poster_asset_id}`) : undefined} className={`${mcls}`} style={boxStyle} controls muted loop playsInline preload="metadata" data-testid={`col-video-${index}`} />;
  else if (cvid) media = <div ref={ref} className={`overflow-hidden ${h ? "" : "aspect-video"}`} style={boxStyle}><iframe className="w-full h-full" src={`https://www.youtube.com/embed/${cvid}`} title="video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>;
  else if (col.image_asset_id) media = <img ref={ref} src={assetUrl(`/api/assets/${col.image_asset_id}`)} alt="" className={`${mcls}`} style={boxStyle} />;
  if (!media) return null;
  return <div className="relative mb-3" style={{ width: w ? `${w}px` : "100%", maxWidth: "100%" }}>{media}{canResize && <CornerHandle onDown={onCorner} testid={`col-resize-${index}`} />}</div>;
}

function GalleryGrid({ block, ctx, props, gitems, colsCls, colCount, gframeCls, gframeBg, gtitleColor, gtitleFont }) {
  const canResize = !!ctx.onUpdateBlock;
  const gridRef = useRef(null);
  const uniform = !!props.uniform_size;
  const below = props.title_pos === "below";
  const fitCls = props.img_fit === "contain" ? "object-contain" : "object-cover";
  const tSize = props.title_size || 0;
  const capColor = props.title_color || undefined;
  return (
    <div ref={gridRef} className={`grid ${colsCls} gap-3 w-full`} style={{ alignItems: "start" }}>{gitems.map((it, i) => {
      const span = uniform ? (props.uni_w || 1) : (it.w || 1);
      const h = uniform ? (props.item_h || 0) : (it.h || props.item_h || 0);
      const commit = (sz) => uniform
        ? ctx.onUpdateBlock(block.id, { uni_w: sz.span, item_h: sz.h })
        : ctx.onUpdateBlock(block.id, { items: gitems.map((x, j) => j === i ? { ...x, w: sz.span, h: sz.h } : x) });
      const hasCap = props.show_titles && (it.title || it.subtitle);
      const belowCaption = hasCap ? (
        <div className="pt-2.5 text-center">
          {it.title && <div className="font-semibold uppercase tracking-wide leading-tight" style={{ color: capColor, fontFamily: gtitleFont, fontSize: tSize ? `${tSize}px` : undefined }}>{it.title}</div>}
          {it.subtitle && <div className="leading-tight opacity-75" style={{ color: capColor, fontFamily: gtitleFont, fontSize: tSize ? `${Math.max(11, tSize - 3)}px` : "0.9rem" }}>{it.subtitle}</div>}
        </div>
      ) : null;
      return (
        <GridMediaItem key={i} canResize={canResize} colCount={colCount} gridRef={gridRef} span={span} h={h} aspectFallback="aspect-square" onCommit={commit}
          cellClassName="group" boxClassName={`overflow-hidden ${gframeCls}`} boxStyle={{ backgroundColor: gframeBg }}
          href={props.enable_links && it.link ? it.link : undefined} interactive={ctx.interactive} after={below ? belowCaption : null} testid={`gallery-resize-${block.id}-${i}`}>
          <img src={assetUrl(`/api/assets/${it.asset_id}`)} alt={it.title || ""} className={`w-full h-full ${fitCls} transition-transform duration-500 group-hover:scale-105`} />
          {!below && hasCap && <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent">{it.title && <span className="text-sm font-semibold block" style={{ color: gtitleColor, fontFamily: gtitleFont }}>{it.title}</span>}{it.subtitle && <span className="text-xs opacity-80 block" style={{ color: gtitleColor, fontFamily: gtitleFont }}>{it.subtitle}</span>}</div>}
        </GridMediaItem>
      );
    })}</div>
  );
}

function CategoriesGrid({ block, ctx, props, items, gridCls, colCount, cframeCls, cframeBg, cimgFit, btnStyle }) {
  const canResize = !!ctx.onUpdateBlock;
  const gridRef = useRef(null);
  const uniform = !!props.uniform_size;
  return (
    <div ref={gridRef} className={`grid ${gridCls} gap-5 w-full`} style={{ alignItems: "start" }}>{items.map((c, i) => {
      const span = uniform ? (props.uni_w || 1) : (c.w || 1);
      const h = uniform ? (props.item_h || 0) : (c.h || 0);
      const commit = (sz) => uniform
        ? ctx.onUpdateBlock(block.id, { uni_w: sz.span, item_h: sz.h })
        : ctx.onUpdateBlock(block.id, { items: items.map((x, j) => j === i ? { ...x, w: sz.span, h: sz.h } : x) });
      const label = c.label ? <span className="inline-block rounded-md px-4 py-1.5 text-xs font-semibold" style={btnStyle}>{c.label}</span> : null;
      return (
        <GridMediaItem key={i} canResize={canResize} colCount={colCount} gridRef={gridRef} span={span} h={h} aspectFallback="aspect-square" onCommit={commit}
          cellClassName="flex flex-col items-center text-center group" boxClassName={`w-full overflow-hidden mb-3 flex items-center justify-center ${cframeCls}`} boxStyle={{ backgroundColor: cframeBg }}
          href={c.link || undefined} interactive={ctx.interactive} after={label} testid={`cat-resize-${block.id}-${i}`}>
          {c.image_asset_id ? <img src={assetUrl(`/api/assets/${c.image_asset_id}`)} alt={c.label} className={`w-full h-full ${cimgFit}`} /> : <Diamond size={32} className="text-neutral-700" />}
        </GridMediaItem>
      );
    })}</div>
  );
}

export function StoreBlock({ block, ctx }) {
  if (block.type === "form") return <FormBlock block={block} ctx={ctx} />;
  if (block.type === "blog") return <BlogBlock block={block} ctx={ctx} />;
  if (block.type === "model3d") return <Model3DBlock block={block} ctx={ctx} />;
  const { style = {}, props = {}, type } = block;
  const { accent, headingFont, products = [], store = {}, titleColor, bodyFont: bodyFontG, btnBg: btnBgG, btnText: btnTextG, btnFont: btnFontG } = ctx;
  const anchorId = block.id ? `blk-${block.id}` : undefined;
  const heading = (txt, cls = "") => txt ? <h2 className={`text-2xl sm:text-3xl font-bold mb-5 ${cls}`} style={{ fontFamily: style.font ? fontStack(style.font) : headingFont, color: style.textColor || titleColor || accent }} data-testid="blk-heading">{txt}</h2> : null;
  const _btnBg = style.btnBg || btnBgG || accent;
  const btnStyle = { backgroundColor: _btnBg, color: style.btnText || btnTextG || textOn(_btnBg), fontFamily: style.btnFont ? fontStack(style.btnFont) : (btnFontG || (style.font ? fontStack(style.font) : undefined)) };
  const bodyStyle = { fontFamily: style.bodyFont ? fontStack(style.bodyFont) : (bodyFontG || undefined), color: style.bodyColor || undefined };
  const innerCls = `${MAXW[style.maxWidth] || MAXW.normal} mx-auto px-6 w-full`;

  // HERO uses its own full-bleed layout
  if (type === "hero") {
    const h = { sm: "h-56 sm:h-72", md: "h-72 sm:h-[26rem]", lg: "h-[22rem] sm:h-[34rem]" }[props.height || "md"];
    return (
      <section id={anchorId} className="relative w-full overflow-hidden" data-testid="blk-hero" style={{ background: style.bg || undefined }}>
        <div className={`relative ${h} w-full`}>
          {(props.video_asset_id || props.video_url)
            ? <BgVideo assetId={props.video_asset_id} url={props.video_url} />
            : props.image_asset_id
            ? <img src={assetUrl(`/api/assets/${props.image_asset_id}`)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${ctx.bg} 0%, ${accent}33 100%)` }} />}
          <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${(props.overlay ?? 45) / 100})` }} />
          <div className={`relative h-full ${MAXW[style.maxWidth] || MAXW.normal} mx-auto px-6 flex flex-col justify-center ${ALIGN[style.align] || ALIGN.left}`}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: style.font ? fontStack(style.font) : headingFont, color: style.textColor || "#fff" }} data-testid="store-name">{props.title}</h1>
            {props.subtitle && <p className="mt-3 text-base sm:text-lg max-w-xl" style={{ color: style.textColor || "rgba(255,255,255,.85)" }}>{props.subtitle}</p>}
            {props.cta_text && <a href={props.cta_link || "#productos"} className="inline-flex items-center gap-2 rounded-full px-6 py-3 mt-6 text-sm font-semibold w-fit" style={btnStyle} data-testid="blk-hero-cta">{props.cta_text} <ArrowRight size={15} /></a>}
          </div>
        </div>
      </section>
    );
  }

  const wrap = (children, extra = {}) => (
    <section id={anchorId} style={sectionStyle(style, ctx)} data-testid={`blk-${type}`} {...extra}>
      <SectionBg style={style} />
      <div className={`${innerCls} flex flex-col ${ALIGN[style.align] || ALIGN.left} ${hasBgVideo(style) ? "relative z-10" : ""}`}>{children}</div>
    </section>
  );

  switch (type) {
    case "text":
      return wrap(<>{heading(props.heading)}{props.body && <p className="leading-relaxed whitespace-pre-line max-w-2xl opacity-90" style={bodyStyle}>{props.body}</p>}</>);
    case "image": {
      const body = <div className="w-full max-w-4xl"><ResizableImage block={block} ctx={ctx} style={style} props={props} />{props.caption && <p className="text-xs opacity-60 mt-2">{props.caption}</p>}</div>;
      return wrap(body);
    }
    case "image_text": {
      const img = props.asset_id
        ? <img src={assetUrl(`/api/assets/${props.asset_id}`)} alt="" className="w-full h-full object-cover rounded-2xl" style={{ borderRadius: style.radius }} />
        : <div className="w-full aspect-square rounded-2xl bg-white/5 border border-dashed border-white/15 flex items-center justify-center text-neutral-500 text-sm">Imagen</div>;
      const text = (
        <div className="flex flex-col justify-center">
          {heading(props.heading, "text-left")}
          {props.body && <p className="leading-relaxed whitespace-pre-line opacity-90" style={bodyStyle}>{props.body}</p>}
          {props.cta_text && <a href={props.cta_link || "#"} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 mt-5 text-sm font-semibold w-fit" style={btnStyle}>{props.cta_text} <ArrowRight size={14} /></a>}
        </div>
      );
      return (
        <section id={anchorId} style={sectionStyle(style, ctx)} data-testid="blk-image_text">
          <div className={`${innerCls} grid md:grid-cols-2 gap-8 items-center`}>
            {props.image_side === "right" ? <>{text}<div>{img}</div></> : <><div>{img}</div>{text}</>}
          </div>
        </section>
      );
    }
    case "products": {
      let list = products;
      if (props.filter === "3d") list = products.filter((p) => !p.catalog_type || p.catalog_type === "custom3d");
      else if (props.filter === "simple") list = products.filter((p) => p.catalog_type && p.catalog_type !== "custom3d");
      if (props.category) list = list.filter((p) => (p.subcategory || "").toLowerCase() === String(props.category).toLowerCase());
      const q = (ctx.searchQuery || "").trim().toLowerCase();
      if (q) list = list.filter((p) => (p.name || "").toLowerCase().includes(q));
      if (Array.isArray(props.order) && props.order.length) {
        const idx = (id) => { const i = props.order.indexOf(id); return i === -1 ? 9999 : i; };
        list = [...list].sort((a, b) => idx(a.id) - idx(b.id));
      }
      if (props.limit > 0) list = list.slice(0, props.limit);
      const cols = responsiveGrid(ctx, props.cols_mobile || 1, props.columns || 3);
      const colCount = props.columns || 3;
      const canResize = !!ctx.onUpdateBlock;
      const uniform = !!props.uniform_size;
      const cardOpts = { img_h: props.card_h || 0, title_pos: props.title_pos || "below", show_badge: props.show_badge !== false, badge_pos: props.badge_pos || "top-left", title_size: props.title_size || 0, price_size: props.price_size || 0, rounded: props.rounded !== false, title_font: props.title_font ? fontStack(props.title_font) : (style.font ? fontStack(style.font) : headingFont), text_align: props.text_align || "left", card_style: props.card_style || "card", show_cta: props.show_cta !== false };
      const sizeFor = (p) => uniform ? { span: props.uni_w || 1, h: props.card_h || 0 } : ((props.item_sizes || {})[p.id] || { span: 1, h: props.card_h || 0 });
      const commitFor = (p) => (sz) => uniform
        ? ctx.onUpdateBlock(block.id, { uni_w: sz.span, card_h: sz.h })
        : ctx.onUpdateBlock(block.id, { item_sizes: { ...(props.item_sizes || {}), [p.id]: { span: sz.span, h: sz.h } } });
      return (
        <section id="productos" style={sectionStyle(style, ctx)} data-testid="blk-products">
          <SectionBg style={style} />
          <div className={`${innerCls} ${hasBgVideo(style) ? "relative z-10" : ""}`}>
            {props.heading && <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: style.font ? fontStack(style.font) : headingFont, color: style.textColor || titleColor || accent, textAlign: style.align }}>{props.heading}</h2>}
            {list.length === 0
              ? <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center text-neutral-500" data-testid="store-empty">Aún no hay productos.</div>
              : <ProductsGrid list={list} ctx={ctx} opts={cardOpts} colCount={colCount} colsCls={cols} canResize={canResize} sizeFor={sizeFor} commitFor={commitFor} />}
          </div>
        </section>
      );
    }
    case "featured_product": {
      const p = products.find((x) => x.id === props.product_id) || products[0];
      if (!p) return wrap(<div className="text-neutral-500 text-sm">Selecciona un producto.</div>);
      return (
        <section id={anchorId} style={sectionStyle(style, ctx)} data-testid="blk-featured_product">
          <div className={`${innerCls} grid md:grid-cols-2 gap-8 items-center`}>
            <div className="aspect-square rounded-2xl overflow-hidden bg-neutral-900/60">
              {(p.base_url || p.image_url) ? <img src={assetUrl(p.base_url || p.image_url)} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Diamond size={48} className="text-neutral-700" /></div>}
            </div>
            <div>
              {props.heading && <p className="text-xs uppercase tracking-widest mb-2" style={{ color: style.textColor || accent, fontFamily: style.font ? fontStack(style.font) : undefined }}>{props.heading}</p>}
              <h2 className="text-3xl font-bold" style={{ fontFamily: style.font ? fontStack(style.font) : headingFont }}>{p.name}</h2>
              <p className="mt-2 text-lg opacity-80">{p.price > 0 ? `$${Number(p.price).toLocaleString()}` : "Personalizable"}</p>
              {ctx.interactive
                ? <Link to={`/tienda/${ctx.slug}/producto/${p.id}`} className="inline-flex items-center gap-2 rounded-full px-6 py-3 mt-6 text-sm font-semibold" style={btnStyle}>Ver producto <ArrowRight size={15} /></Link>
                : <span className="inline-flex items-center gap-2 rounded-full px-6 py-3 mt-6 text-sm font-semibold" style={btnStyle}>Ver producto <ArrowRight size={15} /></span>}
            </div>
          </div>
        </section>
      );
    }
    case "gallery": {
      const glegacy = (props.asset_ids || []).map((id) => ({ asset_id: id }));
      const gitems = (props.items && props.items.length) ? props.items : glegacy;
      const cols = responsiveGrid(ctx, props.cols_mobile || 2, props.columns || 3);
      const gframe = props.frame || "none";
      const gframeCls = gframe === "circle" ? "rounded-full" : gframe === "square" ? "rounded-xl" : (props.rounded === false ? "" : "rounded-xl");
      const gframeBg = gframe === "none" ? "transparent" : (props.frame_color || "rgba(115,115,115,0.4)");
      const gtitleColor = props.title_color || style.textColor || "#ffffff";
      const gtitleFont = style.font ? fontStack(style.font) : headingFont;
      return wrap(
        <>
          {heading(props.heading, style.align === "center" ? "text-center w-full" : "")}
          {gitems.length === 0
            ? <div className="w-full border border-dashed border-white/15 rounded-2xl p-12 text-center text-neutral-500 text-sm">Añade imágenes a la galería.</div>
            : <GalleryGrid block={block} ctx={ctx} props={props} gitems={gitems} colsCls={cols} colCount={props.columns || 3} gframeCls={gframeCls} gframeBg={gframeBg} gtitleColor={gtitleColor} gtitleFont={gtitleFont} />}
        </>
      );
    }
    case "video": {
      const vid = ytId(props.youtube_url);
      return wrap(
        <>
          {heading(props.heading, style.align === "center" ? "text-center w-full" : "")}
          {props.asset_id
            ? <ResizableVideo block={block} ctx={ctx} props={props} />
            : vid
              ? <div className="w-full aspect-video rounded-2xl overflow-hidden"><iframe className="w-full h-full" src={`https://www.youtube.com/embed/${vid}`} title="video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
              : <div className="w-full aspect-video rounded-2xl bg-white/5 border border-dashed border-white/15 flex items-center justify-center text-neutral-500 text-sm gap-2"><PlayCircle size={22} /> Sube un video o pega un enlace de YouTube</div>}
        </>
      );
    }
    case "testimonials": {
      const items = props.items || [];
      return wrap(
        <>
          {heading(props.heading, style.align === "center" ? "text-center w-full" : "")}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
            {items.map((tm, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-left">
                <p className="text-sm italic leading-relaxed opacity-90">“{tm.text}”</p>
                <p className="text-xs mt-3 font-semibold" style={{ color: accent }}>{tm.name}</p>
              </div>
            ))}
          </div>
        </>
      );
    }
    case "button":
      return wrap(
        props.link && ctx.interactive
          ? <a href={props.link} className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-semibold ${props.fullwidth ? "w-full" : "w-fit"}`} style={btnStyle} data-testid="blk-button">{props.text} <ArrowRight size={15} /></a>
          : <span className={`inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-semibold ${props.fullwidth ? "w-full" : "w-fit"}`} style={btnStyle} data-testid="blk-button">{props.text} <ArrowRight size={15} /></span>
      );
    case "columns": {
      const count = props.count || 3;
      const cols = (props.cols || []).slice(0, count);
      const colGrid = responsiveGrid(ctx, props.cols_mobile || (props.mobile_stack === false ? count : 1), count);
      const colRounded = props.rounded !== false;
      const colRad = colRounded ? 18 : 0;
      return wrap(
        <div className={`grid ${colGrid} gap-6 w-full`}>
          {cols.map((c, i) => {
            const colBgVid = !!(c.bg_video_asset_id || c.bg_video_url);
            const colWrap = (c.bg || colBgVid) ? { backgroundColor: c.bg || undefined, padding: "16px", borderRadius: `${colRad}px`, position: colBgVid ? "relative" : undefined, overflow: colBgVid ? "hidden" : undefined } : null;
            return (
            <div key={i} className={`flex flex-col ${ALIGN[style.align] || ALIGN.left}`} style={colWrap}>
              {colBgVid && <><BgVideo assetId={c.bg_video_asset_id} url={c.bg_video_url} /><div className="absolute inset-0 pointer-events-none" style={{ background: `rgba(0,0,0,${(c.bg_video_overlay ?? 30) / 100})` }} /></>}
              <div className={colBgVid ? "relative z-10 w-full flex flex-col" : "w-full flex flex-col"} style={{ alignItems: "inherit" }}>
                <ColumnMedia block={block} ctx={ctx} col={c} index={i} allCols={props.cols || []} rounded={colRounded} />
                {c.heading && <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: style.font ? fontStack(style.font) : headingFont, color: style.textColor || titleColor || accent }}>{c.heading}</h3>}
                {c.body && <p className="text-sm leading-relaxed whitespace-pre-line opacity-85" style={bodyStyle}>{c.body}</p>}
                {c.cta_text && <a href={c.cta_link || "#"} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 mt-3 text-xs font-semibold w-fit" style={btnStyle}>{c.cta_text} <ArrowRight size={13} /></a>}
                {c.form_enabled && <div className={(c.heading || c.body) ? "mt-4 w-full" : "w-full"}><ColumnForm col={c} ctx={ctx} index={i} bodyStyle={bodyStyle} /></div>}
              </div>
            </div>
            );
          })}
        </div>
      );
    }
    case "spacer":
      return <div style={{ height: `${props.height || 48}px`, background: style.bg || undefined }} data-testid="blk-spacer" />;
    case "banners": {
      const count = props.count || 2;
      const items = (props.items || []).slice(0, count);
      const grid = responsiveGrid(ctx, props.cols_mobile || (props.mobile_stack === false ? count : 1), count);
      return (
        <section id={anchorId} style={sectionStyle(style, ctx)} data-testid="blk-banners">
          <div className={`${innerCls} grid ${grid} gap-4`}>
            {items.map((b, i) => {
              const bvid = ytId(b.video_url);
              return (
              <div key={i} className="relative overflow-hidden h-56 sm:h-64 group" style={{ borderRadius: props.rounded === false ? 0 : 12 }} data-testid={`banner-item-${i}`}>
                {b.video_asset_id
                  ? <video src={assetUrl(`/api/assets/${b.video_asset_id}`)} poster={b.poster_asset_id ? assetUrl(`/api/assets/${b.poster_asset_id}`) : undefined} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline preload="metadata" data-testid={`banner-video-${i}`} />
                  : bvid
                    ? <iframe className="absolute inset-0 w-full h-full pointer-events-none" src={`https://www.youtube.com/embed/${bvid}?autoplay=1&mute=1&loop=1&playlist=${bvid}&controls=0&playsinline=1`} title={b.title || "banner"} allow="autoplay; encrypted-media" />
                    : b.image_asset_id
                      ? <img src={assetUrl(`/api/assets/${b.image_asset_id}`)} alt={b.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${ctx.bg} 0%, ${accent}44 100%)` }} />}
                <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${(b.overlay ?? 35) / 100})` }} />
                <div className="absolute inset-0 flex flex-col justify-end p-5">
                  <h3 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: style.font ? fontStack(style.font) : headingFont, color: b.title_color || "#ffffff" }}>{b.title}</h3>
                  {b.cta_text && <a href={b.cta_link || "#"} className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 mt-3 text-xs font-semibold w-fit hover:opacity-90 transition-opacity" style={{ background: b.btn_bg || "rgba(0,0,0,0.7)", color: b.btn_color || "#ffffff" }}>{b.cta_text} <ArrowRight size={13} /></a>}
                </div>
              </div>
              );
            })}
          </div>
        </section>
      );
    }
    case "categories": {
      const count = props.count || 4;
      const items = (props.items || []).slice(0, count);
      const grid = responsiveGrid(ctx, props.cols_mobile || 2, count);
      const cframe = props.frame || "none";
      const cframeCls = cframe === "circle" ? "rounded-full" : cframe === "square" ? "rounded-2xl" : "";
      const cframeBg = cframe === "none" ? "transparent" : (props.frame_color || "rgba(115,115,115,0.4)");
      const cimgFit = cframe === "none" ? "object-contain" : "object-contain p-3";
      return wrap(
        <>
          {heading(props.heading, style.align === "center" ? "text-center w-full" : "")}
          <CategoriesGrid block={block} ctx={ctx} props={props} items={items} gridCls={grid} colCount={count} cframeCls={cframeCls} cframeBg={cframeBg} cimgFit={cimgFit} btnStyle={btnStyle} />
        </>
      );
    }
    case "divider": {
      const w = { narrow: "max-w-xs", normal: "max-w-2xl", wide: "max-w-5xl", full: "w-full" }[props.width || "normal"];
      return <section style={sectionStyle(style, ctx)} data-testid="blk-divider"><div className={`${w} mx-auto`}><hr style={{ borderTopWidth: `${props.thickness || 1}px`, borderColor: props.color || "rgba(128,128,128,.3)" }} /></div></section>;
    }
    case "html":
      return <section style={sectionStyle(style, ctx)} data-testid="blk-html"><div className={innerCls} dangerouslySetInnerHTML={{ __html: props.code || "" }} /></section>;
    default:
      return null;
  }
}

// ---- Ready-made templates (starter block sets + theme) ----
function mk(type, props = {}, style = {}) { const b = newBlock(type); b.props = { ...b.props, ...props }; b.style = { ...b.style, ...style }; return b; }

export const TEMPLATES = [
  {
    key: "minimal_light",
    name: "Minimalista claro",
    desc: "Blanco, limpio y moderno",
    theme: { theme_color: "#ffffff", accent: "#111111", font: "sans" },
    build: () => [
      mk("hero", { title: "Diseño que habla por sí solo", subtitle: "Productos personalizados, hechos para ti.", cta_text: "Ver productos", cta_link: "#productos", overlay: 20, height: "md" }, { align: "center" }),
      mk("text", { heading: "Sobre nosotros", body: "Somos un taller dedicado a crear piezas únicas. Calidad, detalle y un trato cercano en cada pedido." }, { align: "center", maxWidth: "narrow" }),
      mk("columns", { count: 3, cols: [emptyCol("Hecho a mano"), emptyCol("Materiales premium"), emptyCol("Envío a tu puerta")] }, { align: "center" }),
      mk("products", { heading: "Nuestros productos", columns: 3 }),
    ],
  },
  {
    key: "luxury_dark",
    name: "Lujo oscuro",
    desc: "Negro con dorado, elegante",
    theme: { theme_color: "#0B0B0F", accent: "#C6A15B", font: "serif" },
    build: () => [
      mk("hero", { title: "Elegancia personalizada", subtitle: "Joyería y piezas de autor, a tu medida.", cta_text: "Explorar", cta_link: "#productos", overlay: 55, height: "lg" }),
      mk("image_text", { heading: "Artesanía que perdura", body: "Cada pieza se diseña y elabora con los más altos estándares. Un lujo que cuenta tu historia.", image_side: "left" }),
      mk("products", { heading: "Colección", columns: 3 }),
      mk("testimonials", { heading: "Lo que dicen", items: [{ name: "María R.", text: "Una experiencia impecable, la pieza superó mis expectativas." }, { name: "José A.", text: "Atención personalizada de principio a fin." }] }),
    ],
  },
  {
    key: "boutique_warm",
    name: "Boutique cálido",
    desc: "Tonos tierra, acogedor",
    theme: { theme_color: "#201915", accent: "#C98A5E", font: "serif" },
    build: () => [
      mk("hero", { title: "Hecho a mano, con alma", subtitle: "Piezas cálidas y personales para cada ocasión.", cta_text: "Descubrir", cta_link: "#productos", overlay: 45, height: "md" }),
      mk("banners", { count: 2, items: [emptyBanner("Novedades"), emptyBanner("Más vendidos")] }),
      mk("image_text", { heading: "Nuestra historia", body: "Un pequeño taller donde cada detalle importa. Materiales nobles y dedicación artesanal.", image_side: "right" }),
      mk("products", { heading: "Lo nuevo", columns: 3 }),
      mk("form", { heading: "¿Hablamos?", description: "Cuéntanos qué buscas y te ayudamos." }),
    ],
  },
  {
    key: "modern_bold",
    name: "Moderno audaz",
    desc: "Colores vivos, títulos grandes",
    theme: { theme_color: "#0E1116", accent: "#F2552C", font: "sans" },
    build: () => [
      mk("hero", { title: "DISEÑA LO IMPOSIBLE", subtitle: "Personalización 3D sin límites.", cta_text: "Empezar", cta_link: "#productos", overlay: 35, height: "lg" }, { align: "center" }),
      mk("categories", { heading: "Explora por categoría", count: 4, items: [emptyCat("Anillos"), emptyCat("Collares"), emptyCat("Aretes"), emptyCat("Especiales")] }, { align: "center" }),
      mk("products", { heading: "Tendencias", columns: 4 }),
      mk("columns", { count: 3, cols: [emptyCol("Envío rápido"), emptyCol("Garantía total"), emptyCol("Soporte 24/7")] }, { align: "center" }),
    ],
  },
];


// ---- Site-wide Header & Footer ----
export function defaultHeader() {
  return { enabled: true, show_logo: true, title: "", slogan: "", phone: "", show_language: true, show_search: false, show_account: true, show_cart: true, bg: "#111111", textColor: "#ffffff", links: [], logo_asset_id: "", logo_height: 48, announcement: { enabled: false, text: "", bg: "", textColor: "#000000", link: "" } };
}
export function defaultFooter() {
  return { enabled: true, bg: "#111111", textColor: "#cccccc", text: "", columns: [], instagram: "", facebook: "", whatsapp: "" };
}

function navHref(link, ctx) {
  if (link.target_type === "url") return link.url || "#";
  if (link.target_type === "collection") return link.collection ? `/tienda/${ctx.slug}/coleccion/${link.collection}` : "#";
  const ps = link.page_slug || "";
  return ps ? `/tienda/${ctx.slug}/p/${ps}` : `/tienda/${ctx.slug}`;
}

// Available elements that can be placed in a header zone
export const HEADER_ITEM_TYPES = [
  ["logo", "Logo"], ["title", "Título"], ["slogan", "Slogan"], ["menu", "Menú"],
  ["search", "Buscador"], ["account", "Cuenta"], ["cart", "Carrito"],
  ["language", "Idioma"], ["phone", "Teléfono"], ["image", "Imagen propia"],
  ["text", "Texto propio"], ["html", "HTML"], ["spacer", "Espaciador"],
];

// Build a zone-based layout from a legacy header (backward compatible)
export function migrateZones(h) {
  if (h && h.zones) return h.zones;
  const mk = (type, extra = {}) => ({ id: "hi_" + Math.random().toString(36).slice(2, 8), type, ...extra });
  const left = [];
  if ((h.show_logo) !== false) left.push(mk("logo"));
  left.push(mk("title"));
  if (h.slogan) left.push(mk("slogan"));
  const center = [];
  if ((h.links || []).length) center.push(mk("menu"));
  const right = [];
  if (h.phone) right.push(mk("phone"));
  if (h.show_search) right.push(mk("search"));
  if ((h.show_account) !== false) right.push(mk("account"));
  if ((h.show_cart) !== false) right.push(mk("cart"));
  if ((h.show_language) !== false) right.push(mk("language"));
  return { left, center, right };
}

function SearchControl({ ctx }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const doSearch = (e) => {
    e.preventDefault();
    if (!ctx.interactive) return;
    const term = q.trim();
    if (ctx.onSearch) ctx.onSearch(term);
    else window.location.assign(`/tienda/${ctx.slug}${term ? `?q=${encodeURIComponent(term)}` : ""}`);
  };
  return open
    ? <form onSubmit={doSearch} className="flex items-center"><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="h-8 w-32 sm:w-44 rounded-md bg-white/10 border border-white/20 px-2 text-xs outline-none" data-testid="header-search-input" onBlur={() => !q && setOpen(false)} /></form>
    : <button type="button" data-testid="header-search" onClick={() => setOpen(true)} className="opacity-80 hover:opacity-100"><MagnifyingGlass size={18} /></button>;
}

function MenuItems({ h, ctx }) {
  const A = ({ to, ...rest }) => ctx.interactive ? <Link to={to} {...rest} /> : <span {...rest} style={{ ...(rest.style || {}), cursor: "default" }} />;
  if (!(h.links || []).length) return null;
  return (
    <nav className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] font-medium" data-testid="header-nav" style={{ color: ctx.linkColor || undefined, fontFamily: ctx.linkFont || undefined }}>
      {(h.links || []).map((l, i) => {
        const kids = (l.children || []).filter((c) => (c.label || "").trim());
        if (kids.length === 0) return <A key={i} to={navHref(l, ctx)} className="opacity-85 hover:opacity-100 whitespace-nowrap">{l.label}</A>;
        return (
          <div key={i} className="relative group" data-testid={`header-nav-parent-${i}`}>
            <A to={navHref(l, ctx)} className="inline-flex items-center gap-1 opacity-85 group-hover:opacity-100 whitespace-nowrap">{l.label} <CaretDown size={10} weight="bold" className="opacity-70 transition-transform group-hover:rotate-180" /></A>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-40" data-testid={`header-submenu-${i}`}>
              <div className="min-w-[180px] rounded-lg py-1.5 shadow-xl border overflow-hidden" style={{ background: h.bg || "#111", borderColor: "rgba(255,255,255,.14)" }}>
                {kids.map((c, j) => <A key={j} to={navHref(c, ctx)} className="block px-4 py-2 opacity-85 hover:opacity-100 hover:bg-white/10 whitespace-nowrap transition-colors">{c.label}</A>)}
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}

function HeaderItemView({ item, h, ctx }) {
  const store = ctx.store || {};
  const A = ({ to, ...rest }) => ctx.interactive ? <Link to={to} {...rest} /> : <span {...rest} style={{ ...(rest.style || {}), cursor: "default" }} />;
  switch (item.type) {
    case "logo": {
      const id = h.logo_asset_id || store.logo_asset_id;
      if (!id) return null;
      return <img src={assetUrl(`/api/assets/${id}`)} alt="" style={{ height: (h.logo_height || 48) + "px" }} className="object-contain" data-testid="header-logo" />;
    }
    case "title":
      if (!h.title) return null;
      return <div className="text-xl font-bold tracking-wide whitespace-nowrap" style={{ fontFamily: ctx.headingFont }} data-testid="header-title">{h.title}</div>;
    case "slogan":
      if (!h.slogan) return null;
      return <div className="text-[11px] opacity-70 tracking-widest uppercase whitespace-nowrap">{h.slogan}</div>;
    case "menu":
      return <MenuItems h={h} ctx={ctx} />;
    case "search":
      return <SearchControl ctx={ctx} />;
    case "account":
      return <A to={`/tienda/${ctx.slug}/mis-pedidos`} data-testid="header-account" className="opacity-80 hover:opacity-100"><UserCircle size={19} /></A>;
    case "cart":
      return <A to={`/tienda/${ctx.slug}/carrito`} data-testid="header-cart" className="opacity-80 hover:opacity-100"><ShoppingCart size={18} weight="fill" /></A>;
    case "language":
      return (
        <span className="flex items-center gap-1" data-testid="header-lang">
          {["es", "en"].map((lng) => (
            <button key={lng} type="button" onClick={() => ctx.onLang && ctx.onLang(lng)}
              className="px-1.5 py-0.5 rounded uppercase transition-colors cursor-pointer text-xs"
              style={(ctx.lang || ctx.primaryLang || "en") === lng ? { background: ctx.accent, color: "#000" } : { opacity: 0.65 }}
              data-testid={`header-lang-${lng}`}>{lng}</button>
          ))}
        </span>
      );
    case "phone":
      if (!h.phone) return null;
      return <a href={`tel:${h.phone}`} className="inline-flex items-center gap-1.5 text-xs font-semibold underline underline-offset-2 whitespace-nowrap"><Phone size={13} weight="fill" /> {h.phone}</a>;
    case "image": {
      if (!item.asset_id) return null;
      const img = <img src={assetUrl(`/api/assets/${item.asset_id}`)} alt="" style={{ height: (item.height || 40) + "px" }} className="object-contain" data-testid={`header-img-${item.id}`} />;
      return item.link ? <a href={item.link} target="_blank" rel="noreferrer">{img}</a> : img;
    }
    case "text":
      if (!item.text) return null;
      return <span style={{ fontSize: (item.size || 14) + "px", fontWeight: item.bold ? 700 : 400 }} className="whitespace-nowrap" data-testid={`header-text-${item.id}`}>{item.text}</span>;
    case "html":
      if (!item.html) return null;
      return <div dangerouslySetInnerHTML={{ __html: item.html }} data-testid={`header-html-${item.id}`} />;
    case "spacer":
      return <span style={{ display: "inline-block", width: (item.width || 24) + "px" }} />;
    default:
      return null;
  }
}

function HeaderZone({ items, justify, h, ctx }) {
  return (
    <div className={`flex items-center gap-4 flex-wrap ${justify}`}>
      {(items || []).map((it) => <HeaderItemView key={it.id} item={it} h={h} ctx={ctx} />)}
    </div>
  );
}

export function StoreHeader({ header, ctx }) {
  const h = header || {};
  if (h.enabled === false) return null;
  const ann = h.announcement || {};
  const zones = h.zones || migrateZones(h);
  return (
    <header data-testid="store-header" style={{ background: h.bg || "#111", color: h.textColor || "#fff" }}>
      {ann.enabled && ann.text && (
        <div className="text-center text-xs font-medium py-2 px-4" style={{ background: ann.bg || ctx.accent, color: ann.textColor || "#000" }} data-testid="header-announcement">
          {ann.link ? <a href={ann.link} className="hover:underline">{ann.text}</a> : ann.text}
        </div>
      )}
      <div className="max-w-6xl mx-auto px-6 py-3 flex flex-col gap-3 sm:grid sm:grid-cols-3 items-center">
        <HeaderZone items={zones.left} justify="justify-center sm:justify-start" h={h} ctx={ctx} />
        <HeaderZone items={zones.center} justify="justify-center" h={h} ctx={ctx} />
        <HeaderZone items={zones.right} justify="justify-center sm:justify-end" h={h} ctx={ctx} />
      </div>
      {h.bottom && h.bottom.enabled && (h.bottom.columns || []).length > 0 && (
        <div data-testid="header-bottom" style={{ background: h.bottom.bg || "transparent", color: h.bottom.textColor || undefined, borderTop: "1px solid rgba(255,255,255,.12)" }}>
          <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-5 sm:grid sm:gap-8" style={{ gridTemplateColumns: (h.bottom.columns || []).map((c) => `${Math.max(1, c.span || 1)}fr`).join(" ") }}>
            {(h.bottom.columns || []).map((c, i) => {
              const A = ({ to, ...r }) => ctx.interactive ? <Link to={to} {...r} /> : <span {...r} style={{ cursor: "default" }} />;
              const al = c.align === "center" ? "text-center items-center" : c.align === "right" ? "text-right items-end" : "text-left items-start";
              return (
                <div key={c.id || i} className={`flex flex-col ${al}`} data-testid={`header-bottom-col-${i}`}>
                  {c.heading && <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: ctx.accent, fontFamily: ctx.headingFont }}>{c.heading}</div>}
                  {(c.links || []).length > 0 && <nav className="flex flex-col gap-1.5 text-[13px]" style={{ fontFamily: ctx.linkFont || undefined }}>{(c.links || []).filter((l) => (l.label || "").trim()).map((l, j) => <A key={j} to={navHref(l, ctx)} className="opacity-80 hover:opacity-100 transition-opacity" style={{ color: ctx.linkColor || undefined }}>{l.label}</A>)}</nav>}
                  {c.text && <p className="text-xs opacity-75 whitespace-pre-line mt-1 leading-relaxed">{c.text}</p>}
                  {c.image_asset_id && <img src={assetUrl(`/api/assets/${c.image_asset_id}`)} alt="" className="object-contain mt-1" style={{ height: (c.image_height || 48) + "px" }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}

export function StoreFooter({ footer, ctx }) {
  const f = footer || {};
  if (f.enabled === false) return null;
  const cols = f.columns || [];
  return (
    <footer data-testid="store-footer" style={{ background: f.bg || "#111", color: f.textColor || "#ccc" }}>
      <div className="max-w-6xl mx-auto px-6 py-10">
        {cols.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {cols.map((c, i) => (
              <div key={i}>
                <h4 className="font-semibold text-sm mb-2" style={{ color: ctx.accent }}>{c.heading}</h4>
                <ul className="space-y-1 text-xs opacity-80" style={{ fontFamily: ctx.linkFont || undefined }}>{(c.links || []).map((l, j) => <li key={j}><a href={l.url || "#"} className="hover:underline" style={{ color: ctx.linkColor || undefined }}>{l.label}</a></li>)}</ul>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-white/10 pt-5">
          <p className="text-xs opacity-70 whitespace-pre-line">{f.text || ""}</p>
          <div className="flex items-center gap-3">
            {f.instagram && <a href={f.instagram} target="_blank" rel="noreferrer" className="opacity-80 hover:opacity-100"><InstagramLogo size={18} /></a>}
            {f.facebook && <a href={f.facebook} target="_blank" rel="noreferrer" className="opacity-80 hover:opacity-100"><FacebookLogo size={18} /></a>}
            {f.whatsapp && <a href={`https://wa.me/${f.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="opacity-80 hover:opacity-100"><WhatsappLogo size={18} /></a>}
          </div>
        </div>
      </div>
    </footer>
  );
}

